import { createClient } from '@supabase/supabase-js';
import type {
  SupabaseClient,
  SupabaseClientOptions,
} from '@supabase/supabase-js';
import type { Database } from './database-contract';

type DatabaseClientOptions = Database['__InternalSupabase'];
type DatabaseSupabaseClient = SupabaseClient<Database, DatabaseClientOptions>;

// Cache clients by URL so master and tenant can coexist without collisions
// in the Supabase JS client's internal auth / realtime state.
const clients = new Map<string, DatabaseSupabaseClient>();
type SupabaseGlobalOptions = NonNullable<
  SupabaseClientOptions<'public'>['global']
>;
type SupabaseFetch = NonNullable<SupabaseGlobalOptions['fetch']>;

// A cached client keeps one GoTrue instance, but its request router must still
// follow the latest application context. This matters after leaving a static
// platform-admin tenant view: that mode can create the headerless auth client
// before normal workspace mode supplies its late-bound x-lyfty-tenant fetch.
// Store the delegate outside the frozen supabase-js client so call order cannot
// silently discard workspace routing.
const clientFetchDelegates = new Map<string, SupabaseFetch>();

type AuthLock = <T>(
  name: string,
  acquireTimeout: number,
  operation: () => Promise<T>,
) => Promise<T>;

const processAuthLockTails = new Map<string, Promise<void>>();

/**
 * Indefinite auth-storage lock used because supabase-js 2.103 does not forward
 * auth.lockAcquireTimeout to GoTrue. Its default positive timeout steals a
 * Web Lock while the old callback keeps mutating storage.
 */
async function nonStealingAuthLock<T>(
  name: string,
  _acquireTimeout: number,
  operation: () => Promise<T>,
): Promise<T> {
  if (
    typeof globalThis.navigator !== 'undefined' &&
    globalThis.navigator.locks
  ) {
    return globalThis.navigator.locks.request(name, operation);
  }

  const previous = processAuthLockTails.get(name) ?? Promise.resolve();
  const result = previous.then(operation, operation);
  const tail = result.then(
    () => undefined,
    () => undefined,
  );
  processAuthLockTails.set(name, tail);
  void tail.then(() => {
    if (processAuthLockTails.get(name) === tail)
      processAuthLockTails.delete(name);
  });
  return result;
}

export function getSupabaseClient(
  url: string,
  anonKey: string,
  options?: SupabaseClientOptions<'public'>,
): DatabaseSupabaseClient {
  const auth = options?.auth as
    | {
        persistSession?: boolean;
        storage?: unknown;
      }
    | undefined;
  const authMode =
    auth?.persistSession === false
      ? 'no-auth-persistence'
      : auth?.storage
        ? 'custom-auth-storage'
        : 'default-auth';
  const headerEntries = Object.entries(options?.global?.headers ?? {}).sort(
    ([a], [b]) => a.localeCompare(b),
  );
  // `withTenantContext()` deliberately creates an empty headers object when
  // only the late-bound workspace fetch is needed. Treat that exactly like
  // absent headers. Otherwise AuthService's clean auth client and the data
  // client receive different cache keys, so two GoTrue clients compete over
  // the same persisted session and can sign a freshly authenticated user out.
  const headerMode =
    headerEntries.length > 0
      ? JSON.stringify(headerEntries)
      : 'no-global-headers';
  const key = `${url}::${authMode}::${headerMode}`;
  const requestedFetch = options?.global?.fetch;
  if (requestedFetch && !clientFetchDelegates.has(key)) {
    clientFetchDelegates.set(key, requestedFetch);
  }
  let instance = clients.get(key);
  if (!instance) {
    const configuredLock = options?.auth?.lock as AuthLock | undefined;
    const lock: AuthLock = configuredLock
      ? (name, _acquireTimeout, operation) =>
          configuredLock(name, -1, operation)
      : nonStealingAuthLock;
    const routedFetch: SupabaseFetch = (input, init) =>
      (clientFetchDelegates.get(key) ?? globalThis.fetch)(input, init);
    instance = createClient<Database, DatabaseClientOptions>(url, anonKey, {
      ...options,
      auth: {
        ...options?.auth,
        // GoTrue's positive timeout "recovers" by stealing the Web Lock while
        // the prior callback keeps running. The supplied lock ignores that
        // timeout and waits, giving logout a real cross-tab quiescence barrier.
        lock,
      },
      global: {
        ...options?.global,
        // LATE-BOUND fetch — resolved at every request, never captured.
        //
        // supabase-js freezes a reference to the global `fetch` when the
        // client is constructed. This module evaluates before the native
        // bridge installs its connectivity-tracking fetch interceptor
        // (libs/native/*/network.ts), so with the default behaviour NONE of
        // the app's real API traffic was ever observed by the online/offline
        // detector: it only saw OS events, and after a laptop resume the
        // desktop stayed stuck on "Hors-ligne" forever while requests were
        // succeeding — queueing every write into the offline outbox instead
        // of sending it. Deferring to `globalThis.fetch` at call time means
        // whatever interceptor is installed (now or later) always sees the
        // traffic. Callers passing their own global.fetch still win.
        fetch: routedFetch,
      },
    });
    // supabase-js 2.103 drops lockAcquireTimeout while constructing its Auth
    // client. The forwarded non-stealing lock above is the functional guard;
    // patch the internal value as defense in depth and so any future/internal
    // lock replacement still cannot invoke the steal-on-timeout path.
    (
      instance.auth as unknown as { lockAcquireTimeout: number }
    ).lockAcquireTimeout = -1;
    clients.set(key, instance);
  }
  return instance;
}

export type TypedSupabaseClient = DatabaseSupabaseClient;
